/**
 * Tabla de rutas de la app.
 * Cada modulo se carga lazy: solo cuando se navega a su ruta.
 */

import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Spinner } from "@fluentui/react-components";
import { RequirePerm, RequireTecpetrol, RequireAdminPmis } from "./RequirePerm";

const HomePage = lazy(() =>
  import("@features/dashboard/HomePage").then((m) => ({ default: m.HomePage })),
);

const AfeListPage = lazy(() =>
  import("@features/afe/AfeListPage").then((m) => ({ default: m.AfeListPage })),
);

const AfePage = lazy(() =>
  import("@features/afe/AfePage").then((m) => ({ default: m.AfePage })),
);

const CostePage = lazy(() =>
  import("@features/afe/CostePage").then((m) => ({ default: m.CostePage })),
);

const SoListPage = lazy(() =>
  import("@features/so/SoListPage").then((m) => ({ default: m.SoListPage })),
);

const SoCotizacionPage = lazy(() =>
  import("@features/so/SoCotizacionPage").then((m) => ({
    default: m.SoCotizacionPage,
  })),
);

const AutoridadesListPage = lazy(() =>
  import("@features/autoridades/AutoridadesListPage").then((m) => ({
    default: m.AutoridadesListPage,
  })),
);

const EventosListPage = lazy(() =>
  import("@features/eventos/EventosListPage").then((m) => ({
    default: m.EventosListPage,
  })),
);

const EventoPage = lazy(() =>
  import("@features/eventos/EventoPage").then((m) => ({ default: m.EventoPage })),
);

const NotificacionesConfigPage = lazy(() =>
  import("@features/notificaciones/NotificacionesConfigPage").then((m) => ({
    default: m.NotificacionesConfigPage,
  })),
);

const ListaDistribucionPage = lazy(() =>
  import("@features/notificaciones/ListaDistribucionPage").then((m) => ({
    default: m.ListaDistribucionPage,
  })),
);

const ConfiguracionIaPage = lazy(() =>
  import("@features/ia/ConfiguracionIaPage").then((m) => ({
    default: m.ConfiguracionIaPage,
  })),
);

const RiesgosListPage = lazy(() =>
  import("@features/riesgos/RiesgosListPage").then((m) => ({
    default: m.RiesgosListPage,
  })),
);

const RiesgoDetailPage = lazy(() =>
  import("@features/riesgos/RiesgoDetailPage").then((m) => ({
    default: m.RiesgoDetailPage,
  })),
);

const ConfiguracionRiesgosPage = lazy(() =>
  import("@features/riesgos/ConfiguracionRiesgosPage").then((m) => ({
    default: m.ConfiguracionRiesgosPage,
  })),
);

const GerenciasConfigPage = lazy(() =>
  import("@features/gerencias/GerenciasConfigPage").then((m) => ({
    default: m.GerenciasConfigPage,
  })),
);

const StakeholdersListPage = lazy(() =>
  import("@features/stakeholders/StakeholdersListPage").then((m) => ({
    default: m.StakeholdersListPage,
  })),
);

const RdListPage = lazy(() =>
  import("@features/rd/RdListPage").then((m) => ({ default: m.RdListPage })),
);

const RdDetailPage = lazy(() =>
  import("@features/rd/RdDetailPage").then((m) => ({ default: m.RdDetailPage })),
);

const ActasListPage = lazy(() =>
  import("@features/actas/ActasListPage").then((m) => ({ default: m.ActasListPage })),
);

const ActaDetailPage = lazy(() =>
  import("@features/actas/ActaDetailPage").then((m) => ({ default: m.ActaDetailPage })),
);

const ContratosListPage = lazy(() =>
  import("@features/contratos/ContratosListPage").then((m) => ({ default: m.ContratosListPage })),
);

const ContratoPage = lazy(() =>
  import("@features/contratos/ContratoPage").then((m) => ({ default: m.ContratoPage })),
);

const PrListPage = lazy(() =>
  import("@features/pr/PrListPage").then((m) => ({ default: m.PrListPage })),
);

const PrPage = lazy(() =>
  import("@features/pr/PrPage").then((m) => ({ default: m.PrPage })),
);

const PoListPage = lazy(() =>
  import("@features/po/PoListPage").then((m) => ({ default: m.PoListPage })),
);

const PoPage = lazy(() =>
  import("@features/po/PoPage").then((m) => ({ default: m.PoPage })),
);

const RegistroRecursosPage = lazy(() =>
  import("@features/recursos/RegistroRecursosPage").then((m) => ({
    default: m.RegistroRecursosPage,
  })),
);
const MrListPage = lazy(() =>
  import("@features/mr/MrListPage").then((m) => ({ default: m.MrListPage })),
);

const GastosSapPage = lazy(() =>
  import("@features/gastos-sap/GastosSapPage").then((m) => ({ default: m.GastosSapPage })),
);

const MrPage = lazy(() =>
  import("@features/mr/MrPage").then((m) => ({ default: m.MrPage })),
);

const BudgetListPage = lazy(() =>
  import("@features/budget/BudgetListPage").then((m) => ({ default: m.BudgetListPage })),
);

const BudgetPage = lazy(() =>
  import("@features/budget/BudgetPage").then((m) => ({ default: m.BudgetPage })),
);

const GestionReportesPage = lazy(() =>
  import("@features/reportes/GestionReportesPage").then((m) => ({
    default: m.GestionReportesPage,
  })),
);

const BeneficioPage = lazy(() =>
  import("@features/beneficio/BeneficioPage").then((m) => ({
    default: m.BeneficioPage,
  })),
);

const MejorasPage = lazy(() =>
  import("@features/mejoras/MejorasPage").then((m) => ({
    default: m.MejorasPage,
  })),
);

const ListadosPendientesPage = lazy(() =>
  import("@features/comisionamiento/ListadosPendientesPage").then((m) => ({
    default: m.ListadosPendientesPage,
  })),
);

const ListadoPendientesDetailPage = lazy(() =>
  import("@features/comisionamiento/ListadoPendientesDetailPage").then((m) => ({
    default: m.ListadoPendientesDetailPage,
  })),
);

const CostesCatalogoPage = lazy(() =>
  import("@features/costes/CostesCatalogoPage").then((m) => ({
    default: m.CostesCatalogoPage,
  })),
);

export function AppRoutes() {
  return (
    <Suspense fallback={<Spinner label="Cargando..." />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/afe" element={<AfeListPage />} />
        <Route path="/afe/:id" element={<AfePage />} />
        <Route path="/afe/:id/coste/:codigo" element={<CostePage />} />
        <Route path="/so" element={<SoListPage />} />
        <Route path="/so/:id/cotizacion" element={<SoCotizacionPage />} />
        <Route path="/rd" element={<RdListPage />} />
        {/* Gestión del Reporte Diario: misma página de la SO, pero abierta
            desde el módulo de Reportes Diarios habilita la edición del RD. */}
        <Route path="/rd/so/:id/reporte" element={<SoCotizacionPage />} />
        <Route path="/rd/:id" element={<RdDetailPage />} />
        <Route path="/eventos" element={<EventosListPage />} />
        <Route path="/eventos/:id" element={<EventoPage />} />
        <Route path="/actas" element={<ActasListPage />} />
        <Route path="/actas/:id" element={<ActaDetailPage />} />
        <Route path="/contratos" element={<ContratosListPage />} />
        <Route path="/contratos/:id" element={<ContratoPage />} />
        <Route path="/pr" element={<PrListPage />} />
        <Route path="/pr/:id" element={<PrPage />} />
        <Route path="/po" element={<PoListPage />} />
        <Route path="/po/:id" element={<PoPage />} />
        <Route
          path="/recursos"
          element={
            <RequirePerm perm="recursos:view">
              <RegistroRecursosPage />
            </RequirePerm>
          }
        />
        <Route
          path="/mr"
          element={
            <RequirePerm perm="mr:view">
              <MrListPage />
            </RequirePerm>
          }
        />
        <Route
          path="/mr/:id"
          element={
            <RequirePerm perm="mr:view">
              <MrPage />
            </RequirePerm>
          }
        />
        <Route path="/budget" element={<BudgetListPage />} />
        <Route path="/budget/:codigo" element={<BudgetPage />} />
        <Route path="/costes" element={<CostesCatalogoPage />} />
        <Route path="/gastos-sap" element={<GastosSapPage />} />
        <Route
          path="/autoridades"
          element={
            <RequirePerm perm="users:view">
              <AutoridadesListPage />
            </RequirePerm>
          }
        />
        <Route
          path="/configuracion/notificaciones"
          element={
            <RequireAdminPmis>
              <NotificacionesConfigPage />
            </RequireAdminPmis>
          }
        />
        <Route
          path="/configuracion/distribucion"
          element={
            <RequireAdminPmis>
              <ListaDistribucionPage />
            </RequireAdminPmis>
          }
        />
        <Route
          path="/configuracion/ia"
          element={
            <RequireAdminPmis>
              <ConfiguracionIaPage />
            </RequireAdminPmis>
          }
        />
        <Route
          path="/riesgos"
          element={
            <RequireTecpetrol>
              <RequirePerm perm="risk:view">
                <RiesgosListPage />
              </RequirePerm>
            </RequireTecpetrol>
          }
        />
        <Route
          path="/riesgos/:id"
          element={
            <RequireTecpetrol>
              <RequirePerm perm="risk:view">
                <RiesgoDetailPage />
              </RequirePerm>
            </RequireTecpetrol>
          }
        />
        <Route
          path="/configuracion/riesgos"
          element={
            <RequireAdminPmis>
              <ConfiguracionRiesgosPage />
            </RequireAdminPmis>
          }
        />
        <Route
          path="/configuracion/gerencias"
          element={
            <RequireAdminPmis>
              <GerenciasConfigPage />
            </RequireAdminPmis>
          }
        />
        {/* Redirect compat de la ruta vieja (multi-país → multi-gerencia). */}
        <Route
          path="/configuracion/paises"
          element={<Navigate to="/configuracion/gerencias" replace />}
        />
        <Route
          path="/stakeholders"
          element={
            <RequireTecpetrol>
              <RequirePerm perm="stakeholder:view">
                <StakeholdersListPage />
              </RequirePerm>
            </RequireTecpetrol>
          }
        />
        <Route
          path="/comisionamiento"
          element={
            <RequirePerm perm="comm:view">
              <ListadosPendientesPage />
            </RequirePerm>
          }
        />
        <Route
          path="/comisionamiento/:id"
          element={
            <RequirePerm perm="comm:view">
              <ListadoPendientesDetailPage />
            </RequirePerm>
          }
        />
        {/* Beneficio: la validación de acceso (ADMIN_PMIS + allowlist) la
            hace internamente la página vía `puedeVerBeneficio()`. No usamos
            RequirePerm porque el chequeo no es por permiso de rol. */}
        <Route path="/beneficio" element={<BeneficioPage />} />
        <Route
          path="/configuracion/reportes"
          element={<GestionReportesPage />}
        />
        {/* Mejoras PMIS — abierto a todos los roles autenticados (perm
            `mejoras:view`). Cualquiera reporta, solo el admin responde. */}
        <Route
          path="/mejoras"
          element={
            <RequirePerm perm="mejoras:view">
              <MejorasPage />
            </RequirePerm>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
